import { apiFetch, apiFetchResponse } from "@/lib/api-client";
import { isFundLiveHere } from "@/lib/env-visibility";
import type { Fund as DdbFund } from "@etfsite/db/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Public fund list — backed by `/api/data/funds` (DDB + Valkey cache, ~1 h TTL).
 *
 * Returns raw `DdbFund[]` with zero field-dropping. All consumers (FundCard,
 * HomePage, ProductsPage) read from the DDB shape directly:
 *   - `fund.id`        for the URL slug
 *   - `fund.fundType`  for category comparisons
 *   - `fundImageUrl(fund.imageKeys[0])` for the card image
 *
 * Feature flags (isLiveProduction, showPerformanceChart, showHoldingsTable,
 * featuredOnHomepage, etc.) all live on the DDB fund record — no separate
 * feature_flags API. Use `useFundConfig(ticker)` to read a single fund by ticker.
 */

interface ApiFundsResponse {
  funds: DdbFund[];
}

/**
 * Public fund list — filtered to funds live in THIS environment.
 *
 * Drafts (`isLive{Env} !== true`) are excluded everywhere: home page grid,
 * products listing, ticker tape, counts. They remain in DDB so admin can
 * promote them via the Copy-to-Env button, but never reach the public site
 * until that flag is flipped.
 */
export function useFundList() {
  return useQuery({
    queryKey: ["funds"],
    queryFn: () =>
      apiFetch<ApiFundsResponse>("/api/data/funds").then((r) => r.funds.filter(isFundLiveHere)),
    staleTime: 60 * 60 * 1000, // 1 hour — matches API cache TTL
  });
}

/**
 * Fetch a single fund by slug from the lightweight `/api/data/funds/:ticker`
 * endpoint (~10 KB) instead of downloading the whole `/api/data/funds` list
 * (~2.7 MB). The smaller, faster payload is what makes a direct landing — most
 * importantly Googlebot rendering `/euv` — resolve reliably, which is the root
 * cause of fund pages being intermittently excluded from the index.
 *
 * Return contract (consumed by FundDetailPage to decide `noindex`):
 *   - `DdbFund`  → the fund exists and is live in this env.
 *   - `null`     → the fund DEFINITIVELY does not exist / is not live (unknown
 *                  ticker resolves to the SPA HTML fallback, or a 404). The
 *                  detail page renders a `noindex` not-found card.
 *   - throws     → INDETERMINATE (network failure / 5xx). The detail page keeps
 *                  the page indexable so a transient blip never deindexes a real
 *                  fund; TanStack Query also retries.
 */
async function fetchFundBySlug(slug: string): Promise<DdbFund | null> {
  const res = await apiFetchResponse(`/api/data/funds/${encodeURIComponent(slug)}`);

  // 5xx → indeterminate: surface as an error (no noindex, query retries).
  if (res.status >= 500) {
    throw new Error(`API ${res.status} for /api/data/funds/${slug}`);
  }

  // Unknown ticker → CloudFront serves the SPA shell (200 text/html), or a 404.
  // Either way the fund definitively doesn't exist: resolve to null (→ noindex),
  // never throw, so it isn't confused with a transient server error.
  const contentType = res.headers.get("content-type") ?? "";
  if (res.status === 404 || !contentType.includes("application/json")) {
    return null;
  }

  const body = (await res.json()) as { fund?: DdbFund | null };
  const fund = body.fund ?? null;
  // Hide non-live funds from public lookup — detail page treats null as 404.
  return fund && isFundLiveHere(fund) ? fund : null;
}

/**
 * Look up a single DDB fund by URL slug (`fund.id`).
 *
 * Fast path: reuse the shared `["funds"]` query cache — no extra round-trip when
 * the (already live-filtered) list has loaded (e.g. user came from home/products).
 * Direct landing (deep link / crawler): fetch just that one fund, not the list.
 */
export function useFundBySlug(slug: string | null | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["funds", "by-slug", slug?.toLowerCase()],
    queryFn: () => {
      const id = slug?.toLowerCase() ?? "";
      const cached = queryClient.getQueryData<DdbFund[]>(["funds"]);
      // The `["funds"]` cache is already filtered to funds live in this env, so a
      // miss here is a definitive not-found (→ null), not an error.
      if (cached) return cached.find((f) => f.id === id) ?? null;
      return fetchFundBySlug(id);
    },
    enabled: Boolean(slug),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Single raw DDB fund config by ticker.
 *
 * Returns the full `DdbFund` record. Used by `FundDetailPage` to read feature
 * flags directly from the fund record:
 *   - `fund.isLiveProduction` — env-visibility gate
 *   - `fund.showPerformanceChart` — performance chart section
 *   - `fund.showHoldingsTable` — holdings table section
 *   - `fund.showDailyNav` — P/D chart section
 *   - `fund.featuredOnHomepage` — featured grid on home page
 */
export function useFundConfig(ticker: string | null | undefined) {
  return useQuery({
    queryKey: ["fund", ticker?.toUpperCase()],
    queryFn: () =>
      apiFetch<{ fund: DdbFund }>(`/api/data/funds/${ticker?.toUpperCase()}`).then((r) => r.fund),
    enabled: Boolean(ticker),
    staleTime: 60 * 60 * 1000,
    // 404 = fund not in DDB yet — not an error, resolve to undefined.
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("404")) return false;
      return failureCount < 1;
    },
  });
}
